import { useEffect, useRef, useState } from 'react';
import { buildTextField, type TextField, type TextFieldStats } from '../core/text/textField';
import { textFieldCacheKey } from '../core/text/settings';
import { scheduleFrame, type ScheduledTask } from '../core/schedule';
import { useStudio } from '../store/studioStore';
import { useTextSettings } from './useTextSettings';

export interface TextFieldResult {
  field: TextField | null;
  stats: TextFieldStats | null;
  error: string | null;
  building: boolean;
}

/**
 * Rebuilds the text field whenever its *geometry* inputs change, and only then.
 *
 * The cache key deliberately excludes purely visual settings, so switching a layer to
 * stroke or toggling the inspector never triggers an SDF recomputation. Builds are
 * coalesced to one per frame, so dragging a slider does not queue one rebuild per
 * pointer event.
 */
export function useTextField(): TextFieldResult {
  const settings = useTextSettings();
  const canvas = useStudio((s) => s.doc.canvas);
  const font = useStudio((s) => s.font);

  const [result, setResult] = useState<TextFieldResult>({ field: null, stats: null, error: null, building: false });
  const lastKey = useRef<string>('');
  const task = useRef<ScheduledTask | null>(null);

  useEffect(() => {
    if (!font) return;
    const key = `${font.id}|${textFieldCacheKey(settings, canvas.width, canvas.height)}`;
    if (key === lastKey.current) return;

    task.current?.cancel();
    setResult((r) => ({ ...r, building: true }));

    task.current = scheduleFrame(() => {
      try {
        const { field, stats } = buildTextField({
          settings,
          font,
          width: canvas.width,
          height: canvas.height,
        });
        lastKey.current = key;
        setResult({ field, stats, error: null, building: false });
      } catch (err) {
        setResult((r) => ({ ...r, error: (err as Error).message, building: false }));
      }
    });

    return () => task.current?.cancel();
  }, [settings, canvas.width, canvas.height, font]);

  return result;
}
